import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, useSearchParams } from "react-router-dom";

import DomainNotRegisteredPage from "../../pages/DomainNotRegisteredPage";
import HostedPageError from "../../pages/HostedPageError";
import SessionExpiredOverlay from "./SessionExpiredOverlay";
import HostedCheckoutSkeleton from "./HostedCheckoutSkeleton";

import { useCheckoutPage } from "../../hooks/useCheckoutPage";
import {
  createSession,
  startHostedPageSession,
  fetchSession,
  fetchSessionDetails,
  loadApiKeys,
  updateSession,
} from "../../utils/paymentSession";
import {
  fetchtLargeUrl,
  resolveCheckoutToken,
  resolveHostedPage,
  resumeHostedCheckoutSession,
  resumeDynamicHostedCheckoutSession,
} from "../../api/api";
import { parseHostedPaymentReturn, clearHostedPaymentReturnParams } from "../../utils/hostedPaymentReturn";
import { getShopperCitFailureMessage } from "../../utils/paymentOutcome";
import { useTranslation } from "../../hooks/useTranslation";
import { useLanguage } from "../../context/LanguageContext";
import {
  mergeFormDataFromCheckoutDto,
  extractSelectedAddonsFromCheckoutDto,
  extractCheckoutDataFromSessionDto,
  isValidUuid,
  normalizeGatewayPlatform,
} from "../../utils/sessionHydration";
import {
  clearCachedSession,
  getCachedSession,
  setCachedSession,
} from "../../utils/hostedSessionCache";

import { getSkin } from "../../skins/registry";
import { useCheckout } from "../../context/CheckoutContext";
import { normalizeUrlProtocol } from "../../utils/redirectUrlBuilder";
import { resolveHostedPageErrorType, isConfigurationIssue } from "../../utils/hostedPageError";
import { filterRoutingEntriesByCurrency } from "../../utils/routingEntries";
import { normalizeSessionTrial } from "../../utils/trialCheckout";

// Per-session checkout TTL (must match backend application.session.expiry.in.seconds).
const SESSION_TTL_SECONDS = 15 * 60;

const CheckoutPage = () => {
  /* =====================================================
     ROUTER
  ===================================================== */
  const { shortKey, configId, sessionId: urlSessionId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const tenantId = window.location.hostname.split(".")[0];

  const checkoutMode = configId
    ? "hostedConfig"
    : urlSessionId
      ? "hostedSession"
      : "legacy";
  const isHostedConfigMode = checkoutMode === "hostedConfig";
  const isHostedSessionMode = checkoutMode === "hostedSession";
  const isHostedSurface = isHostedConfigMode || isHostedSessionMode;
  const { t } = useTranslation();
  const { applySessionLocale, beginCheckoutSession } = useLanguage();
  const {
    setIsHostedPageMode,
    setCitPaymentErrorMessage,
    setHostedPaymentSelection,
    setPendingPaymentResume,
    setSessionTrial,
    setForceTrialPeriodDays,
    merchantName,
    logoUrl,
  } = useCheckout();

  const hostedPaymentReturn = useMemo(
    () => (isHostedSurface ? parseHostedPaymentReturn(searchParams) : null),
    [isHostedSurface, searchParams]
  );

  useEffect(() => {
    setIsHostedPageMode(isHostedSurface);
  }, [isHostedSurface, setIsHostedPageMode]);

  const applySessionTrialContext = (details) => {
    const cart = extractCheckoutDataFromSessionDto(details?.dto);
    const trial = normalizeSessionTrial(details?.trial || cart?.trialMetadata);
    setSessionTrial(trial);
    setForceTrialPeriodDays(cart?.forceTrialPeriodDays ?? null);
  };

  const applyPendingPaymentResume = (details) => {
    const pending = details?.pendingPayment;
    if (!pending?.paymentResponse) return;

    setPendingPaymentResume({
      paymentMethod: pending.paymentMethod,
      platform: normalizeGatewayPlatform(pending.platform),
      paymentResponse: pending.paymentResponse,
      transactionId: pending.transactionId,
    });

    if (pending.paymentMethod && pending.platform) {
      setHostedPaymentSelection({
        method: pending.paymentMethod,
        platform: normalizeGatewayPlatform(pending.platform),
      });
    }
  };

  useEffect(() => {
    if (!isHostedSessionMode) {
      setSessionTrial(null);
      setForceTrialPeriodDays(null);
    }
  }, [isHostedSessionMode, setSessionTrial, setForceTrialPeriodDays]);

  /* =====================================================
     STATE
  ===================================================== */
  const [checkoutData, setCheckoutData] = useState({
    billingPlatformId: null,
    planId: null,
    frequency: null,
    price: null,
    currency: null,
    addons: [],
    couponCodes: [],
  });

  const [isLoadingShortUrl, setIsLoadingShortUrl] = useState(
    !!shortKey && checkoutMode === "legacy"
  );
  const [sessionId, setSessionId] = useState(null);

  // Hosted page / session resolution lifecycle state
  const [isResolvingConfig, setIsResolvingConfig] = useState(isHostedSurface);
  const [hostedPageError, setHostedPageError] = useState(null); // { type }

  const reportHostedPageError = (status, message) => {
    setHostedPageError({
      type: resolveHostedPageErrorType(status, message),
      // Only allowlisted configuration messages are shown verbatim; every
      // other error type keeps its generic, shopper-safe copy.
      message: isConfigurationIssue(message) ? message : null,
    });
  };

  const applyLocaleFromSessionDetails = (sessionId, details) => {
    if (hostedSessionLanguageInitRef.current === sessionId) return;
    hostedSessionLanguageInitRef.current = sessionId;
    beginCheckoutSession(sessionId);

    const locale = details?.locale || details?.dto?.locale;
    if (locale) {
      applySessionLocale(locale);
    }
  };
  const [sessionExpired, setSessionExpired] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(SESSION_TTL_SECONDS);
  const [sessionStartedAt, setSessionStartedAt] = useState(null);
  const [expiresAtEpochMs, setExpiresAtEpochMs] = useState(null);
  const [restartNonce, setRestartNonce] = useState(0);

  // Guards against re-creating a session when deps hydrate after the first
  // render. Hosted: configId:restartNonce. Legacy: bp/plan/currency/coupons/addons.
  const sessionInitKeyRef = useRef(null);
  const pendingResumeRef = useRef(null);
  const resumeHydratedRef = useRef(false);
  const hostedSessionLanguageInitRef = useRef(null);

  /* =====================================================
     HELPERS
  ===================================================== */
  const parseParams = (params) => {
    const couponCandidates = [
      ...params.getAll("coupon"),
      ...params.getAll("coupon_code"),
      ...params.getAll("coupon_codes"),
    ];

    const normalizedCouponCodes = Array.from(
      new Set(
        couponCandidates
          .flatMap((value) => String(value).split(","))
          .map((code) => code?.trim())
          .filter((code) => !!code)
      )
    );

    return {
      billingPlatformId: params.get("billing_platform"),
      planId: params.get("plan_id"),
      frequency: params.get("frequency"),
      price: params.get("price"),
      currency: params.get("currency"),
      addons: params.getAll("addon").map((a) => {
        const [id, qty] = a.split(":");
        return { itemPriceId: id, quantity: Number(qty) || 1 };
      }),
      couponCodes: normalizedCouponCodes,
    };
  };

  const normalizeCheckoutPayload = (data) => {
    const normalizedCouponCodes = Array.from(
      new Set(
        (data.coupon || [])
          .flatMap((c) => String(c).split(","))
          .map((code) => code?.trim())
          .filter(Boolean)
      )
    );

    const normalizedAddons = (data.addon || []).map((a) => ({
      itemPriceId: a.id,
      quantity: Number(a.qty) || 1,
    }));

    return {
      billingPlatformId: data.billing_Platform,
      planId: data.plan_Id,
      frequency: data.frequency,
      currency: data.currency,
      addons: normalizedAddons,
      couponCodes: normalizedCouponCodes,
    };
  };

  /* =====================================================
     FETCH LONG URL (if shortKey exists)
  ===================================================== */
  useEffect(() => {
    if (isHostedSurface) {
      setIsLoadingShortUrl(false);
      return;
    }
    const loadCheckoutData = async () => {
      try {
        if (!shortKey) {
          const queryParams = new URLSearchParams(location.search);
          setCheckoutData(parseParams(queryParams));
          setIsLoadingShortUrl(false);
          return;
        }

        const res = await fetchtLargeUrl(shortKey);

        if (res?.longUrl) {
          const url = new URL(normalizeUrlProtocol(res.longUrl));
          setCheckoutData(parseParams(url.searchParams));
        }
      } catch (err) {
        console.error("Error fetching redirect URL:", err);
      } finally {
        setIsLoadingShortUrl(false);
      }
    };

    loadCheckoutData();
  }, [shortKey, location.search]);

  /* =====================================================
     RESOLVE CHECKOUT TOKEN (if token param exists)
  ===================================================== */
  useEffect(() => {
    if (isHostedSurface || !searchParams.get("token")) {
      return;
    }
    setIsLoadingShortUrl(true);
    const loadCheckoutData = async () => {
      try {
        const res = await resolveCheckoutToken(searchParams.get("token"));
        if (res?.data) {
          setCheckoutData(normalizeCheckoutPayload(res.data));
        }
      } catch (err) {
        console.error("Error fetching redirect URL:", err);
      } finally {
        setIsLoadingShortUrl(false);
      }
    };

    loadCheckoutData();
  }, [searchParams.get("token"), location.search, isHostedSurface]);

  /* =====================================================
     RESOLVE HOSTED PAGE CONFIG (opaque UUID flow)
  ===================================================== */
  useEffect(() => {
    if (!isHostedConfigMode) return;

    let cancelled = false;
    const resolveConfig = async () => {
      setIsResolvingConfig(true);
      setHostedPageError(null);
      try {
        const res = await resolveHostedPage(configId);
        if (cancelled) return;

        const addonItems = (res?.lineItems || [])
          .filter((li) => li.type === "addon")
          .map((li) => ({ itemPriceId: li.id, quantity: li.quantity || 1 }));

        setCheckoutData({
          billingPlatformId: res?.billingPlatformId || null,
          planId: res?.planId || null,
          frequency: res?.frequency || null,
          price: null,
          currency: res?.currency || null,
          addons: addonItems,
          couponCodes: res?.couponCodes || [],
        });
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const message =
          err?.response?.data?.message || err?.response?.data?.error || null;
        reportHostedPageError(status, message);
      } finally {
        if (!cancelled) setIsResolvingConfig(false);
      }
    };

    resolveConfig();
    return () => {
      cancelled = true;
    };
  }, [isHostedConfigMode, configId]);

  /* =====================================================
     RESOLVE DYNAMIC HOSTED SESSION (/checkout/s/{sessionId})
  ===================================================== */
  useEffect(() => {
    if (!isHostedSessionMode) return;

    if (!isValidUuid(urlSessionId)) {
      setHostedPageError({ type: "not_found" });
      setIsResolvingConfig(false);
      return;
    }

    let cancelled = false;

    const resolveSession = async () => {
      setIsResolvingConfig(true);
      setHostedPageError(null);

      const startSession = (id, createdAt, remainingSeconds, expiresAtMs) => {
        setSessionId(id);
        setSessionStartedAt(createdAt);
        setSecondsLeft(remainingSeconds);
        setExpiresAtEpochMs(expiresAtMs);
        setSessionExpired(false);
      };

      try {
        if (hostedPaymentReturn?.trxId) {
          try {
            const resume = await resumeDynamicHostedCheckoutSession(
              urlSessionId,
              hostedPaymentReturn.trxId
            );
            if (cancelled) return;
            if (resume?.session_id) {
              const details = await fetchSessionDetails(resume.session_id);
              if (cancelled || !details?.dto) return;
              applyLocaleFromSessionDetails(resume.session_id, details);
              setCheckoutData(extractCheckoutDataFromSessionDto(details.dto));
              applySessionTrialContext(details);
              applyPendingPaymentResume(details);
              const createdAt = resume.created_at_epoch_ms || Date.now();
              const expiresAt =
                resume.expires_at_epoch_ms ||
                createdAt + SESSION_TTL_SECONDS * 1000;
              const remaining = resume.remaining_seconds ?? SESSION_TTL_SECONDS;
              startSession(resume.session_id, createdAt, remaining, expiresAt);
              pendingResumeRef.current = { dto: details.dto };
              setCitPaymentErrorMessage(getShopperCitFailureMessage());
              if (resume.payment_method && resume.platform) {
                setHostedPaymentSelection({
                  method: resume.payment_method,
                  platform: normalizeGatewayPlatform(resume.platform),
                });
              }
              clearHostedPaymentReturnParams();
              return;
            }
          } catch (resumeErr) {
            console.warn(
              "Could not resume dynamic hosted session after payment failure:",
              resumeErr
            );
          }
          if (cancelled) return;
        }

        const details = await fetchSessionDetails(urlSessionId);
        if (cancelled) return;
        if (!details?.dto) {
          setHostedPageError({ type: "not_found" });
          return;
        }

        applyLocaleFromSessionDetails(urlSessionId, details);

        const cart = extractCheckoutDataFromSessionDto(details.dto);
        if (!cart?.billingPlatformId || !cart?.planId) {
          setHostedPageError({ type: "error" });
          return;
        }

        setCheckoutData(cart);
        applySessionTrialContext(details);
        applyPendingPaymentResume(details);
        pendingResumeRef.current = { dto: details.dto, initial: true };
        const createdAt = details.createdAtEpochMs || Date.now();
        const expiresAt =
          details.expiresAtEpochMs || createdAt + SESSION_TTL_SECONDS * 1000;
        const remaining =
          details.remainingSeconds ??
          Math.max(1, Math.round((expiresAt - Date.now()) / 1000));
        startSession(urlSessionId, createdAt, remaining, expiresAt);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const message =
          err?.response?.data?.message || err?.response?.data?.error || null;
        reportHostedPageError(status, message);
      } finally {
        if (!cancelled) setIsResolvingConfig(false);
      }
    };

    resolveSession();
    return () => {
      cancelled = true;
    };
  }, [isHostedSessionMode, urlSessionId, hostedPaymentReturn?.trxId, beginCheckoutSession, applySessionLocale]);

  /* =====================================================
     DERIVED VALUES
  ===================================================== */
  const {
    billingPlatformId,
    planId,
    frequency,
    price,
    currency,
    addons,
    couponCodes,
  } = checkoutData;

  const parsedAddonsFromURL = useMemo(() => {
    return addons.map((a) => ({
      itemPriceId: a.itemPriceId,
      quantity: a.quantity,
    }));
  }, [addons]);

  /** Stable keys so legacy session create does not re-fire on array identity churn. */
  const legacySessionAddonKey = useMemo(
    () =>
      JSON.stringify(
        (parsedAddonsFromURL || []).map((a) => ({
          id: a.itemPriceId,
          q: Number(a.quantity) || 1,
        }))
      ),
    [parsedAddonsFromURL]
  );

  const legacySessionCouponKey = useMemo(
    () => JSON.stringify([...(couponCodes || [])].map(String).sort()),
    [couponCodes]
  );

  /* =====================================================
     MAIN CHECKOUT HOOK
  ===================================================== */
  const {
    checkoutPageConfDto,
    checkoutPageFields,
    routingEntries,
    platformItemDetails,
    isErrorModalVisible,
    itemDetails,
    showModal,
    transactionStatus,
    domainStatus,
    currentDomain,
    isLoading,
    formData,
    isFormValid,
    isRoutingLoaded,
    transactionId,
    isPaymentProcessingModalVisible,
    paymentProcessingHeader,
    paymentProcessingMessage,
    billingPlatformId: hookBillingPlatformId,
    planId: hookPlanId,
    addons: hookAddons,
    selectedAddons,
    isTransactionDetailsModalVisible,
    setIsTransactionDetailsModalVisible,
    setErrorModalVisible,
    setShowModal,
    setTransactionStatus,
    setIsPaymentProcessingModalVisible,
    handleFormChange,
    handleValidationChange,
    handleSubmit,
    setSelectedAddons,
    setCheckoutPageConfDto,
  } = useCheckoutPage({
    initialBillingPlatformId: billingPlatformId,
    initialPlanId: planId,
    initialFrequency: frequency,
    initialPrice: price,
    initialCurrency: currency,
    initialAddons: parsedAddonsFromURL,
    sessionId,
    // Wait for session in every mode so catalog is fetched once with sessionId
    // (legacy previously fetched without it, then again when session arrived).
    catalogRequiresSessionId: true,
  });

  const currentCurrency = itemDetails?.currencyCode;

  const hasNoPaymentMethods =
    !isLoading &&
    isRoutingLoaded &&
    Array.isArray(routingEntries) &&
    // Require currency when methods are currency-scoped so we don't flash the
    // unavailable page before plan currency is known.
    Boolean(currentCurrency) &&
    filterRoutingEntriesByCurrency(routingEntries, currentCurrency).length === 0;

  /** Session create must use URL/config addons (known synchronously), not
   *  hydrated selectedAddons — otherwise create runs twice as addons sync. */
  const buildLegacySessionLineItems = () => {
    if (!planId) return [];

    return [
      { type: "plan", id: planId, quantity: 1 },
      ...(parsedAddonsFromURL || []).map((addon) => ({
        type: "addon",
        id: addon.itemPriceId,
        quantity: Number(addon.quantity) || 1,
      })),
    ];
  };

  /* =====================================================
     LOAD API KEYS
  ===================================================== */
  useEffect(() => {
    if (!tenantId) return;
    loadApiKeys(tenantId);
  }, [tenantId]);

  /* =====================================================
     CREATE SESSION
  ===================================================== */
  useEffect(() => {
    if (isHostedSessionMode) return;
    if (!billingPlatformId || !planId) return;

    let cancelled = false;

    const startSession = (id, createdAt, remainingSeconds) => {
      setSessionId(id);
      setSessionStartedAt(createdAt);
      setSecondsLeft(remainingSeconds);
      setSessionExpired(false);
    };

    const init = async () => {
      if (isHostedConfigMode) {
        // Only initialize once per (config, restart). Do not set the guard until
        // a session is actually established — otherwise a re-run triggered when
        // selectedAddons hydrates can cancel the in-flight create and leave
        // sessionId null (hiding the TTL badge).
        const initKey = `${configId}:${restartNonce}`;
        if (sessionInitKeyRef.current === initKey) return;

        if (hostedPaymentReturn?.trxId) {
          try {
            const resume = await resumeHostedCheckoutSession(
              configId,
              hostedPaymentReturn.trxId
            );
            if (cancelled) return;
            if (resume?.session_id) {
              const sessionUuid = resume.session_id;
              const dto = await fetchSession(sessionUuid);
              if (cancelled) return;
              const createdAt = resume.created_at_epoch_ms || Date.now();
              const expiresAt =
                resume.expires_at_epoch_ms ||
                createdAt + SESSION_TTL_SECONDS * 1000;
              const remaining = resume.remaining_seconds ?? SESSION_TTL_SECONDS;
              setCachedSession(configId, sessionUuid, createdAt, expiresAt);
              startSession(sessionUuid, createdAt, remaining);
              sessionInitKeyRef.current = initKey;
              pendingResumeRef.current = { dto };
              setCitPaymentErrorMessage(getShopperCitFailureMessage());
              if (resume.payment_method && resume.platform) {
                setHostedPaymentSelection({
                  method: resume.payment_method,
                  platform: normalizeGatewayPlatform(resume.platform),
                });
              }
              clearHostedPaymentReturnParams();
              return;
            }
          } catch (resumeErr) {
            console.warn("Could not resume hosted session after payment failure:", resumeErr);
          }
          if (cancelled) return;
        }

        // Reuse a still-valid session from a previous load (e.g. refresh),
        // re-validating it server-side before trusting the local cache.
        const cached = getCachedSession(configId);
        if (cached) {
          try {
            const details = await fetchSessionDetails(cached.sessionId);
            if (cancelled) return;
            if (details?.dto) {
              applySessionTrialContext(details);
              applyPendingPaymentResume(details);
              const remaining =
                details.remainingSeconds ??
                Math.max(1, Math.round((cached.expiresAt - Date.now()) / 1000));
              startSession(cached.sessionId, cached.createdAt, remaining);
              sessionInitKeyRef.current = initKey;
              return;
            }
          } catch (_e) {
            clearCachedSession(configId);
          }
          if (cancelled) return;
        }

        let startPayload;
        try {
          startPayload = await startHostedPageSession(configId);
        } catch (sessionErr) {
          if (cancelled) return;
          reportHostedPageError(
            sessionErr?.status ?? sessionErr?.response?.status,
            sessionErr?.backendMessage || sessionErr?.message
          );
          return;
        }
        if (cancelled) return;
        if (!startPayload?.session_id) {
          setHostedPageError((prev) => prev || { type: "error" });
          return;
        }
        const id = startPayload.session_id;
        const createdAt = startPayload.created_at_epoch_ms || Date.now();
        const expiresAt =
          startPayload.expires_at_epoch_ms ||
          createdAt + SESSION_TTL_SECONDS * 1000;
        const remaining = startPayload.remaining_seconds ?? SESSION_TTL_SECONDS;
        setCachedSession(configId, id, createdAt, expiresAt);
        startSession(id, createdAt, remaining);
        sessionInitKeyRef.current = initKey;
        return;
      }

      // Legacy (/checkout?... , short keys, tokens): create once from URL cart.
      // Do not depend on selectedAddons — it hydrates after mount and was
      // causing a second sessions/create (first often without addons).
      const initKey = `legacy:${billingPlatformId}:${planId}:${currency || ""}:${legacySessionCouponKey}:${legacySessionAddonKey}`;
      if (sessionInitKeyRef.current === initKey) return;
      // Set before await so overlapping effect runs with the same cart skip.
      sessionInitKeyRef.current = initKey;

      const lineItems = buildLegacySessionLineItems();
      if (!lineItems.length) {
        sessionInitKeyRef.current = null;
        return;
      }

      const id = await createSession(
        currency,
        lineItems,
        billingPlatformId,
        couponCodes
      );
      if (cancelled) return;

      if (!id) {
        sessionInitKeyRef.current = null;
        setSessionId(null);
        return;
      }

      if (couponCodes?.length) {
        try {
          await updateSession(id, { billingData: { couponCodes } });
        } catch (syncError) {
          console.warn("Failed to persist preset coupon codes on session:", syncError);
        }
      }

      if (cancelled) return;
      startSession(id, Date.now(), SESSION_TTL_SECONDS);
    };

    init();
    return () => {
      cancelled = true;
    };
    // Legacy: use stable addon/coupon keys, never selectedAddons (hydrates later).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    billingPlatformId,
    planId,
    currency,
    legacySessionCouponKey,
    legacySessionAddonKey,
    isHostedConfigMode,
    isHostedSessionMode,
    configId,
    restartNonce,
    hostedPaymentReturn?.trxId,
  ]);

  /* =====================================================
     HYDRATE FORM FROM SESSION (initial load + failed-payment return)
  ===================================================== */
  useEffect(() => {
    if (!sessionId || resumeHydratedRef.current) return;
    const pending = pendingResumeRef.current;
    if (!pending?.dto) return;
    handleFormChange(mergeFormDataFromCheckoutDto(pending.dto));
    const addonsFromSession = extractSelectedAddonsFromCheckoutDto(pending.dto);
    if (addonsFromSession.length) {
      setSelectedAddons(addonsFromSession);
    }
    resumeHydratedRef.current = true;
    pendingResumeRef.current = null;
  }, [sessionId, handleFormChange, setSelectedAddons]);

  /* =====================================================
     SESSION EXPIRY COUNTDOWN
  ===================================================== */
  useEffect(() => {
    if (!sessionStartedAt || sessionExpired) return;

    const tick = () => {
      if (expiresAtEpochMs) {
        const remaining = Math.max(
          0,
          Math.floor((expiresAtEpochMs - Date.now()) / 1000)
        );
        if (remaining <= 0) {
          setSecondsLeft(0);
          setSessionExpired(true);
        } else {
          setSecondsLeft(remaining);
        }
        return;
      }

      const elapsed = Math.floor((Date.now() - sessionStartedAt) / 1000);
      const remaining = SESSION_TTL_SECONDS - elapsed;
      if (remaining <= 0) {
        setSecondsLeft(0);
        setSessionExpired(true);
      } else {
        setSecondsLeft(remaining);
      }
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [sessionStartedAt, sessionExpired, expiresAtEpochMs]);

  const handleRestartSession = () => {
    if (!isHostedConfigMode) return;
    clearCachedSession(configId);
    setSessionExpired(false);
    setSessionId(null);
    setSessionStartedAt(null);
    setExpiresAtEpochMs(null);
    setSecondsLeft(SESSION_TTL_SECONDS);
    resumeHydratedRef.current = false;
    pendingResumeRef.current = null;
    setCitPaymentErrorMessage(null);
    setHostedPaymentSelection(null);
    setPendingPaymentResume(null);
    setRestartNonce((n) => n + 1);
  };

  /* =====================================================
     RENDER GUARDS
  ===================================================== */
  if (hostedPageError) {
    return (
      <HostedPageError
        errorType={hostedPageError.type}
        message={hostedPageError.message}
        merchantName={
          merchantName ||
          checkoutPageConfDto?.companyName ||
          checkoutPageConfDto?.brand
        }
        logoUrl={logoUrl || checkoutPageConfDto?.logoUrl}
        returnUrl={checkoutPageConfDto?.checkoutRedirectUrl}
      />
    );
  }

  const isCheckoutBootstrapping =
    isLoadingShortUrl ||
    isResolvingConfig ||
    domainStatus === "checking" ||
    isLoading ||
    !isRoutingLoaded;

  if (isCheckoutBootstrapping) {
    return isHostedSurface ? <HostedCheckoutSkeleton /> : null;
  }

  if (domainStatus !== "valid") {
    return <DomainNotRegisteredPage domain={currentDomain} />;
  }

  // Hosted pages: after catalog loads with no routable methods (e.g. merchant
  // deactivated the only gateway), show the same unavailable screen as when
  // /start rejects — not an infinite payment-method skeleton.
  if (isHostedSurface && hasNoPaymentMethods) {
    const currencyLabel = currentCurrency || "this currency";
    return (
      <HostedPageError
        errorType="unavailable"
        message={`No payment methods available for currency: ${currencyLabel}`}
        merchantName={
          merchantName ||
          checkoutPageConfDto?.companyName ||
          checkoutPageConfDto?.brand
        }
        logoUrl={logoUrl || checkoutPageConfDto?.logoUrl}
        returnUrl={checkoutPageConfDto?.checkoutRedirectUrl}
      />
    );
  }

  /* =====================================================
     SKIN DISPATCH
  ===================================================== */
  const { component: SkinComponent } = getSkin(checkoutPageConfDto?.hpSkinCode);

  const resolvedBillingPlatformId = hookBillingPlatformId || billingPlatformId;
  const resolvedPlanId = hookPlanId || planId;

  const countdownLabel = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")}`;

  return (
    <>
      {sessionExpired && (
        <SessionExpiredOverlay
          onRestart={handleRestartSession}
          canRestart={isHostedConfigMode}
          merchantName={
            merchantName ||
            checkoutPageConfDto?.companyName ||
            checkoutPageConfDto?.brand
          }
          returnUrl={checkoutPageConfDto?.checkoutRedirectUrl}
        />
      )}
      {isHostedSurface &&
        sessionId &&
        !sessionExpired &&
        checkoutPageConfDto?.showHostedPageExpirationTimer && (
        <div
          style={{
            position: "fixed",
            top: 12,
            right: 12,
            zIndex: 1050,
            background: secondsLeft <= 60 ? "#fde2e1" : "#eef2f7",
            color: secondsLeft <= 60 ? "#b3261e" : "#334155",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: 999,
            padding: "6px 12px",
            fontSize: 13,
            fontWeight: 600,
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
          title="Time remaining to complete this checkout"
        >
          Session expires in {countdownLabel}
        </div>
      )}
      <SkinComponent
        checkoutPageConfDto={checkoutPageConfDto}
      checkoutPageFields={checkoutPageFields}
      routingEntries={routingEntries}
      platformItemDetails={platformItemDetails}
      itemDetails={itemDetails}
      formData={formData}
      isFormValid={isFormValid}
      billingPlatformId={resolvedBillingPlatformId}
      planId={resolvedPlanId}
      addons={hookAddons}
      selectedAddons={selectedAddons}
      sessionId={sessionId}
      couponCodes={couponCodes}
      isLoading={isLoading}
      isRoutingLoaded={isRoutingLoaded}
      hasNoPaymentMethods={hasNoPaymentMethods}
      showModal={showModal}
      transactionStatus={transactionStatus}
      transactionId={transactionId}
      isErrorModalVisible={isErrorModalVisible}
      isPaymentProcessingModalVisible={isPaymentProcessingModalVisible}
      paymentProcessingHeader={paymentProcessingHeader}
      paymentProcessingMessage={paymentProcessingMessage}
      isTransactionDetailsModalVisible={isTransactionDetailsModalVisible}
      handleFormChange={handleFormChange}
      handleValidationChange={handleValidationChange}
      handleSubmit={handleSubmit}
      setShowModal={setShowModal}
      setTransactionStatus={setTransactionStatus}
      setErrorModalVisible={setErrorModalVisible}
      setIsPaymentProcessingModalVisible={setIsPaymentProcessingModalVisible}
      setIsTransactionDetailsModalVisible={setIsTransactionDetailsModalVisible}
      setCheckoutPageConfDto={setCheckoutPageConfDto}
      />
    </>
  );
};

export default CheckoutPage;
